/**
 * HP tag helpers  (DA-2297 / SM-30)
 *
 * The catalog encodes each HP value's mapper fragment in `values[].tag`
 * (`HP:<column>:<op>:<code>`) — e.g. attribute "Property Type" (`attributeId: propertyType`),
 * value "Detached" (`id: detached`) → tag `HP:dwelling:D:6`. The mapper COLUMN (`dwelling`)
 * and value CODE (`6`) live ONLY in the tag; the display `attributeId`/value `id`/`name` must
 * never reach a mapper string. A clause that serialized `HP:propertyType:D:detached` would be
 * a broken mapper — pingpong expects `HP:dwelling:D:6` (read) / `HP:dwelling:6` (write).
 *
 * These helpers decode the tag so the HP pickers store `dwelling`/`6`, not `propertyType`/
 * `detached`. They also fold the (rare) case of two catalog attributes mapping to the SAME
 * column — e.g. residential `occupants` (D) and SMB `employees` (C) both → `numOccupants` —
 * by keying everything off the column and aggregating each column's operators/values.
 */
import { HomeProfileAttributeCatalog } from 'types/apis/surveyInterfaces'

// HP:<column>:<D|C>:<code>. Column may contain dots/brackets (e.g.
// `userBusinessType.businessType`, `BusinessTimings.BusinessDays[MONDAY].workingDay`) but no
// colon; the operator is the single D/C letter between the last two colons the column leaves.
const HP_TAG_RE = /^HP:(.+):([DC]):(.+)$/

export interface ParsedHpTag {
  column: string
  operator: 'D' | 'C'
  code: string
}

export function parseHpTag(tag?: string | null): ParsedHpTag | null {
  const m = tag ? HP_TAG_RE.exec(tag) : null
  return m ? { column: m[1], operator: m[2] as 'D' | 'C', code: m[3] } : null
}

/** The mapper column an attribute's values share (from the tag); attributeId is a display-only fallback. */
export function attributeColumn(attribute?: HomeProfileAttributeCatalog): string {
  if (!attribute) return ''
  for (const v of attribute.values ?? []) {
    const p = parseHpTag(v.tag)
    if (p) return p.column
  }
  return attribute.attributeId
}

/** Read operators an attribute actually uses, from its value tags. */
export function attributeOperators(attribute?: HomeProfileAttributeCatalog): Set<'D' | 'C'> {
  const ops = new Set<'D' | 'C'>()
  for (const v of attribute?.values ?? []) {
    const p = parseHpTag(v.tag)
    if (p) ops.add(p.operator)
  }
  return ops
}

/** Discrete value options for one attribute: `{ value: <tag code>, label: <display name> }`. */
export function discreteOptions(attribute?: HomeProfileAttributeCatalog): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = []
  for (const v of attribute?.values ?? []) {
    const p = parseHpTag(v.tag)
    if (p?.operator === 'D') out.push({ value: p.code, label: v.name })
  }
  return out
}

/** All catalog attributes that resolve to a given mapper column. */
export function attributesForColumn(
  attributes: HomeProfileAttributeCatalog[] | undefined,
  column: string,
): HomeProfileAttributeCatalog[] {
  return (attributes ?? []).filter((a) => attributeColumn(a) === column)
}

/** Union of read operators across every attribute mapping to `column`. */
export function columnOperators(attributes: HomeProfileAttributeCatalog[] | undefined, column: string): Set<'D' | 'C'> {
  const ops = new Set<'D' | 'C'>()
  for (const a of attributesForColumn(attributes, column)) attributeOperators(a).forEach((o) => ops.add(o))
  return ops
}

/** Distinct discrete `{ value: code, label }` options across every attribute mapping to `column`. */
export function columnDiscreteOptions(
  attributes: HomeProfileAttributeCatalog[] | undefined,
  column: string,
): { value: string; label: string }[] {
  const seen = new Set<string>()
  const out: { value: string; label: string }[] = []
  for (const a of attributesForColumn(attributes, column)) {
    for (const o of discreteOptions(a)) {
      if (seen.has(o.value)) continue
      seen.add(o.value)
      out.push(o)
    }
  }
  return out
}

/** Distinct HP columns for the attribute picker, labelled by the first attribute that maps to each column. */
export function hpColumnOptions(
  attributes: HomeProfileAttributeCatalog[] | undefined,
): { value: string; label: string }[] {
  const seen = new Set<string>()
  const out: { value: string; label: string }[] = []
  for (const a of attributes ?? []) {
    const col = attributeColumn(a)
    if (!col || seen.has(col)) continue
    seen.add(col)
    out.push({ value: col, label: a.label || a.attributeId })
  }
  return out
}
